/**
 * SCIBRS / NIBRS code tables, checked against SLED's SCIBRS Manual v2025.0 (page refs below).
 * South Carolina adds offense codes, data values and edits to the FBI's NIBRS; "SC" marks those.
 * Each code carries a plain-English label and everyday search words so deputies never need to know the code.
 * Search only suggests codes; the deputy always picks (SCIBRS Manual p.9: no defaults, no automatic coding).
 */

export type CrimeAgainst = 'person' | 'property' | 'society' | 'none';
export type OffenseGroup = 'A' | 'B' | 'NC'; // NC = not a crime (information-only reports)

export interface OffenseDef {
  code: string;
  name: string;
  /** What a deputy would call it. */
  plain: string;
  against: CrimeAgainst;
  group: OffenseGroup;
  /** Search words: "break in", "stole my car", "fight"… */
  words: string[];
  category: 'violent' | 'sex' | 'theft' | 'fraud' | 'damage' | 'drugs' | 'weapons' | 'vice' | 'other' | 'arrest-only' | 'non-criminal';
  /** Reported to SCIBRS as Group A but NOT a crime (09C, 979, 980, 978, 992): left out of crime totals and clearance rates. */
  notCrime?: boolean;
  /** No arrest can be reported for it (09C, 979, 978, 992; 980 only when attempted). */
  noArrest?: boolean;
  /** South Carolina-only code (SCIBRS adds it; SLED handles it for the FBI). */
  sc?: boolean;
}

export const OFFENSES: OffenseDef[] = [
  // Crimes against persons
  { code: '09A', name: 'Murder/Nonnegligent Manslaughter', plain: 'Murder', against: 'person', group: 'A', category: 'violent', words: ['homicide', 'killed', 'murder', 'shot dead', 'death'] },
  { code: '09B', name: 'Negligent Manslaughter', plain: 'Negligent manslaughter', against: 'person', group: 'A', category: 'violent', words: ['accidental killing', 'reckless death'] },
  { code: '09C', name: 'Justifiable Homicide', plain: 'Justifiable homicide (not a crime)', against: 'none', group: 'A', category: 'non-criminal', notCrime: true, noArrest: true, words: ['self defense killing', 'justifiable homicide'] },
  { code: '100', name: 'Kidnapping/Abduction', plain: 'Kidnapping / abduction', against: 'person', group: 'A', category: 'violent', words: ['kidnap', 'abduct', 'took child', 'held against will', 'custodial interference'] },
    // 2025: 11A–11C are all "Rape" now; South Carolina keeps the three codes and SLED converts them for the FBI (pp.22, 39–41).
  { code: '11A', name: 'Rape – Sexual Intercourse', plain: 'Rape (sexual intercourse)', against: 'person', group: 'A', category: 'sex', words: ['rape', 'sexual assault', 'csc', 'criminal sexual conduct'] },
  { code: '11B', name: 'Rape – Sodomy', plain: 'Rape (sodomy)', against: 'person', group: 'A', category: 'sex', words: ['sodomy', 'sexual assault', 'csc', 'oral sex forced', 'anal'] },
  { code: '11C', name: 'Rape – Sexual Assault with an Object', plain: 'Rape (with an object)', against: 'person', group: 'A', category: 'sex', words: ['sexual assault object', 'csc', 'penetration object'] },
  { code: '11D', name: 'Criminal Sexual Contact', plain: 'Criminal sexual contact (groping)', against: 'person', group: 'A', category: 'sex', words: ['fondling', 'groped', 'touched', 'lewd act', 'csc', 'sexual contact', 'unwanted touching'] },
  { code: '36A', name: 'Incest', plain: 'Incest', against: 'person', group: 'A', category: 'sex', words: ['incest'] },
  { code: '36B', name: 'Statutory Rape', plain: 'Statutory rape', against: 'person', group: 'A', category: 'sex', words: ['statutory', 'underage sex'] },
  { code: '36C', name: 'Sexual Exposure', plain: 'Indecent exposure (sexual)', against: 'person', group: 'A', category: 'sex', sc: true, words: ['exposed himself', 'exposed herself', 'exposure', 'flashing', 'indecent exposure', 'masturbating in public', 'lewd'] },
  { code: '13A', name: 'Aggravated Assault', plain: 'Aggravated assault (weapon or serious injury)', against: 'person', group: 'A', category: 'violent', words: ['shot', 'stabbed', 'cut', 'shooting', 'assault weapon', 'abhan', 'assault and battery high', 'pointed gun', 'serious injury', 'strangled', 'choked'] },
  { code: '13B', name: 'Simple Assault', plain: 'Simple assault (fight, hit, no serious injury)', against: 'person', group: 'A', category: 'violent', words: ['fight', 'hit', 'punched', 'slapped', 'pushed', 'assault', 'battery', 'domestic', 'cdv', 'kicked'] },
  { code: '13C', name: 'Intimidation', plain: 'Threats / intimidation', against: 'person', group: 'A', category: 'violent', words: ['threat', 'threatened', 'intimidation', 'stalking', 'menacing', 'texts threatening', 'threatened to kill'] },
  { code: '753', name: 'Telephone Calls, Obscene, Harassing', plain: 'Harassing or obscene calls / texts', against: 'person', group: 'A', category: 'other', sc: true, words: ['harassing calls', 'obscene calls', 'harassing texts', 'keeps texting', 'keeps calling', 'facebook messages', 'harassment', 'harassing messages'] },
  { code: '64A', name: 'Human Trafficking, Commercial Sex Acts', plain: 'Human trafficking (sex)', against: 'person', group: 'A', category: 'sex', words: ['trafficking', 'pimping'] },
  { code: '64B', name: 'Human Trafficking, Involuntary Servitude', plain: 'Human trafficking (labor)', against: 'person', group: 'A', category: 'violent', words: ['trafficking', 'forced labor'] },
  // South Carolina-only, not crimes (pp.43–45): reported to SCIBRS, left out of crime totals.
  { code: '979', name: 'Missing Persons', plain: 'Missing person (unexplained)', against: 'none', group: 'A', category: 'non-criminal', sc: true, notCrime: true, noArrest: true, words: ['missing', 'missing person', 'disappeared', 'can’t locate', 'cant locate', 'missing adult'] },
  { code: '980', name: 'Suicide', plain: 'Suicide or suicide attempt', against: 'none', group: 'A', category: 'non-criminal', sc: true, notCrime: true, words: ['suicide', 'killed himself', 'killed herself', 'attempted suicide', 'tried to kill himself', 'tried to kill herself'] },
  // Crimes against property
  { code: '120', name: 'Robbery', plain: 'Robbery (took property by force or threat)', against: 'property', group: 'A', category: 'violent', words: ['robbery', 'robbed', 'armed robbery', 'held up', 'mugged', 'strong arm', 'carjacking'] },
  { code: '200', name: 'Arson', plain: 'Arson (set on fire on purpose)', against: 'property', group: 'A', category: 'damage', words: ['arson', 'set fire', 'set on fire', 'burned on purpose'] },
  { code: '978', name: 'Suspicious Fires', plain: 'Suspicious fire (cause not known yet)', against: 'none', group: 'A', category: 'non-criminal', sc: true, notCrime: true, noArrest: true, words: ['suspicious fire', 'fire', 'burned', 'fire unknown cause', 'fire under investigation'] },
  { code: '210', name: 'Extortion/Blackmail', plain: 'Extortion / blackmail', against: 'property', group: 'A', category: 'fraud', words: ['extortion', 'blackmail', 'sextortion'] },
  { code: '220', name: 'Burglary/Breaking and Entering', plain: 'Burglary / break-in', against: 'property', group: 'A', category: 'theft', words: ['burglary', 'break in', 'broke in', 'breaking and entering', 'kicked in door', 'shed', 'house broken into', 'b&e'] },
  { code: '23A', name: 'Pocket-picking', plain: 'Pickpocket', against: 'property', group: 'A', category: 'theft', words: ['pickpocket', 'wallet taken'] },
  { code: '23B', name: 'Purse-snatching', plain: 'Purse snatching', against: 'property', group: 'A', category: 'theft', words: ['purse snatch', 'grabbed purse'] },
  { code: '23C', name: 'Shoplifting', plain: 'Shoplifting', against: 'property', group: 'A', category: 'theft', words: ['shoplifting', 'shoplift', 'stole from store', 'walked out without paying'] },
  { code: '23D', name: 'Theft From Building', plain: 'Theft from a building', against: 'property', group: 'A', category: 'theft', words: ['stole from building', 'theft inside'] },
  { code: '23E', name: 'Theft From Coin-Operated Machine or Device', plain: 'Theft from a coin machine', against: 'property', group: 'A', category: 'theft', words: ['vending machine', 'coin machine', 'arcade'] },
  { code: '23F', name: 'Theft From Motor Vehicle', plain: 'Theft from a vehicle (car break-in)', against: 'property', group: 'A', category: 'theft', words: ['car broken into', 'stole from car', 'vehicle break in', 'smash and grab'] },
  { code: '23G', name: 'Theft of Motor Vehicle Parts or Accessories', plain: 'Theft of vehicle parts', against: 'property', group: 'A', category: 'theft', words: ['catalytic converter', 'tires stolen', 'wheels stolen', 'tag stolen', 'license plate stolen'] },
  { code: '23H', name: 'All Other Larcenies', plain: 'Theft / larceny (other)', against: 'property', group: 'A', category: 'theft', words: ['theft', 'stole', 'stolen', 'larceny', 'took', 'missing', 'package stolen', 'trailer stolen', 'gas drive off', 'drive off'] },
  { code: '240', name: 'Motor Vehicle Theft', plain: 'Stolen vehicle', against: 'property', group: 'A', category: 'theft', words: ['stolen car', 'stole my car', 'vehicle stolen', 'truck stolen', 'atv stolen', 'motorcycle stolen', 'auto theft', 'truck', 'motorcycle', 'atv', 'golf cart', 'dirt bike', 'took my car', 'took the car'] },
  { code: '756', name: 'Using Motor Vehicle Without Consent', plain: 'Took a vehicle without permission (brought back)', against: 'property', group: 'A', category: 'theft', sc: true, words: ['joyride', 'joy ride', 'took my car without asking', 'unauthorized use', 'son took car', 'daughter took car', 'brought it back'] },
  { code: '250', name: 'Counterfeiting/Forgery', plain: 'Forgery / counterfeiting', against: 'property', group: 'A', category: 'fraud', words: ['forgery', 'forged', 'counterfeit', 'fake money', 'fake check'] },
  { code: '26A', name: 'False Pretenses/Swindle/Confidence Game', plain: 'Scam / swindle', against: 'property', group: 'A', category: 'fraud', words: ['scam', 'swindle', 'con', 'fraud', 'gift cards', 'contractor fraud', 'took money no work'] },
  { code: '26B', name: 'Credit Card/Automated Teller Machine Fraud', plain: 'Credit / debit card fraud', against: 'property', group: 'A', category: 'fraud', words: ['credit card fraud', 'debit card', 'atm fraud', 'card used'] },
  { code: '26C', name: 'Impersonation', plain: 'Impersonation', against: 'property', group: 'A', category: 'fraud', words: ['impersonation', 'pretended to be'] },
  { code: '26D', name: 'Welfare Fraud', plain: 'Welfare fraud', against: 'property', group: 'A', category: 'fraud', words: ['welfare', 'snap fraud', 'ebt'] },
  { code: '26E', name: 'Wire Fraud', plain: 'Wire fraud', against: 'property', group: 'A', category: 'fraud', words: ['wire fraud', 'wire transfer'] },
  { code: '26F', name: 'Identity Theft', plain: 'Identity theft', against: 'property', group: 'A', category: 'fraud', words: ['identity theft', 'someone used my name', 'ssn used', 'opened account in my name'] },
  { code: '26G', name: 'Hacking/Computer Invasion', plain: 'Hacking / computer invasion', against: 'property', group: 'A', category: 'fraud', words: ['hacked', 'hacking', 'account hacked', 'computer'] },
  { code: '270', name: 'Embezzlement', plain: 'Embezzlement', against: 'property', group: 'A', category: 'fraud', words: ['embezzlement', 'employee stole', 'breach of trust'] },
  { code: '280', name: 'Stolen Property Offenses', plain: 'Receiving / possessing stolen property', against: 'property', group: 'A', category: 'theft', words: ['receiving stolen', 'possession of stolen', 'pawned stolen'] },
  { code: '290', name: 'Destruction/Damage/Vandalism of Property', plain: 'Vandalism / damage to property', against: 'property', group: 'A', category: 'damage', words: ['vandalism', 'damage', 'damaged', 'malicious injury', 'broke window', 'keyed car', 'graffiti', 'slashed tires'] },
  { code: '510', name: 'Bribery', plain: 'Bribery', against: 'property', group: 'A', category: 'fraud', words: ['bribe', 'bribery'] },
  // Crimes against society
  { code: '35A', name: 'Drug/Narcotic Violations', plain: 'Drugs (possession, sale, making)', against: 'society', group: 'A', category: 'drugs', words: ['drugs', 'marijuana', 'meth', 'cocaine', 'crack', 'fentanyl', 'heroin', 'pills', 'possession', 'pwid', 'trafficking drugs', 'weed'] },
  { code: '35B', name: 'Drug Equipment Violations', plain: 'Drug paraphernalia', against: 'society', group: 'A', category: 'drugs', words: ['paraphernalia', 'pipe', 'needles', 'scales', 'bong'] },
  { code: '370', name: 'Pornography/Obscene Material', plain: 'Pornography / obscene material', against: 'society', group: 'A', category: 'vice', words: ['pornography', 'obscene', 'child pornography', 'sexual exploitation of a minor'] },
  { code: '39A', name: 'Betting/Wagering', plain: 'Illegal betting', against: 'society', group: 'A', category: 'vice', words: ['betting', 'wagering'] },
  { code: '39B', name: 'Operating/Promoting/Assisting Gambling', plain: 'Running gambling', against: 'society', group: 'A', category: 'vice', words: ['gambling', 'video poker'] },
  { code: '39C', name: 'Gambling Equipment Violations', plain: 'Gambling machines / equipment', against: 'society', group: 'A', category: 'vice', words: ['gambling machine', 'video poker machine'] },
  { code: '39D', name: 'Sports Tampering', plain: 'Sports tampering', against: 'society', group: 'A', category: 'vice', words: ['sports tampering'] },
  { code: '40A', name: 'Prostitution', plain: 'Prostitution', against: 'society', group: 'A', category: 'vice', words: ['prostitution'] },
  { code: '40B', name: 'Assisting or Promoting Prostitution', plain: 'Promoting prostitution', against: 'society', group: 'A', category: 'vice', words: ['pimp', 'promoting prostitution'] },
  { code: '40C', name: 'Purchasing Prostitution', plain: 'Buying sex', against: 'society', group: 'A', category: 'vice', words: ['solicitation', 'john'] },
  { code: '520', name: 'Weapon Law Violations', plain: 'Weapons violation', against: 'society', group: 'A', category: 'weapons', words: ['gun', 'weapon', 'unlawful carry', 'felon with gun', 'possession of weapon', 'discharging firearm', 'shots fired'] },
  { code: '992', name: 'Prowler', plain: 'Prowler', against: 'none', group: 'A', category: 'non-criminal', sc: true, notCrime: true, noArrest: true, words: ['prowler', 'lurking', 'peeping', 'someone in yard', 'looking in windows', 'peeping tom'] },
  { code: '720', name: 'Animal Cruelty', plain: 'Animal cruelty', against: 'society', group: 'A', category: 'other', words: ['animal cruelty', 'neglected dog', 'starving horse', 'ill treatment of animals', 'dog fighting'] },
  // Group B: reported to the state only when someone is arrested
  { code: '90B', name: 'Curfew/Loitering/Vagrancy Violations', plain: 'Curfew / loitering', against: 'society', group: 'B', category: 'arrest-only', words: ['curfew', 'loitering', 'vagrancy'] },
  { code: '90C', name: 'Disorderly Conduct', plain: 'Disorderly conduct', against: 'society', group: 'B', category: 'arrest-only', words: ['disorderly', 'public disorderly', 'disturbance', 'drunk in public', 'breach of peace'] },
  { code: '90D', name: 'Driving Under the Influence', plain: 'DUI', against: 'society', group: 'B', category: 'arrest-only', words: ['dui', 'dwi', 'drunk driving', 'impaired driving'] },
  { code: '90F', name: 'Family Offenses-Nonviolent', plain: 'Family offense, nonviolent (neglect, nonsupport)', against: 'society', group: 'B', category: 'arrest-only', words: ['child neglect', 'unlawful neglect', 'nonsupport', 'abandonment'] },
  { code: '90G', name: 'Alcohol Law Violations', plain: 'Alcohol / liquor law violation', against: 'society', group: 'B', category: 'arrest-only', words: ['underage drinking', 'minor in possession', 'open container', 'alcohol', 'liquor'] },
  { code: '90I', name: 'Runaway', plain: 'Runaway (under 18)', against: 'society', group: 'B', category: 'arrest-only', sc: true, words: ['runaway', 'ran away', 'left home', 'didn’t come home', 'didnt come home'] },
  { code: '90J', name: 'Trespass of Real Property', plain: 'Trespassing', against: 'society', group: 'B', category: 'arrest-only', words: ['trespass', 'trespassing', 'no trespassing'] },
  { code: '90K', name: 'Incorrigible Child', plain: 'Incorrigible / ungovernable child', against: 'society', group: 'B', category: 'arrest-only', sc: true, words: ['incorrigible', 'ungovernable', 'out of control child', 'beyond control'] },
  { code: '90L', name: 'Truancy', plain: 'Truancy', against: 'society', group: 'B', category: 'arrest-only', sc: true, words: ['truant', 'truancy', 'skipping school', 'not going to school'] },
  { code: '90N', name: 'Resisting Arrest', plain: 'Resisting arrest', against: 'society', group: 'B', category: 'arrest-only', sc: true, words: ['resisting', 'resisted', 'resisting arrest', 'pulled away', 'fled from officer'] },
  { code: '90P', name: 'Contributing to the Delinquency of a Minor', plain: 'Contributing to the delinquency of a minor', against: 'society', group: 'B', category: 'arrest-only', sc: true, words: ['contributing', 'contributing to delinquency', 'gave alcohol to minor', 'gave beer to minor'] },
  { code: '90Z', name: 'All Other Offenses', plain: 'Other offense (warrant, obstruction, etc.)', against: 'society', group: 'B', category: 'arrest-only', words: ['warrant', 'bench warrant', 'obstruction', 'probation violation', 'contempt', 'failure to appear', 'other'] },
  // Not a crime: information-only reports (not sent to the state)
  { code: 'NC-INFO', name: 'Information Only', plain: 'Information only / no crime', against: 'none', group: 'NC', category: 'non-criminal', words: ['information', 'info', 'documentation', 'civil matter', 'civil dispute'] },
  { code: 'NC-DOMESTIC', name: 'Domestic Disturbance (No Crime)', plain: 'Domestic disturbance, no crime', against: 'none', group: 'NC', category: 'non-criminal', words: ['domestic', 'argument', 'verbal', 'family dispute'] },
  { code: 'NC-SUSPICIOUS', name: 'Suspicious Activity', plain: 'Suspicious person / vehicle / activity', against: 'none', group: 'NC', category: 'non-criminal', words: ['suspicious', 'suspicious vehicle', 'suspicious person'] },
  { code: 'NC-FOUND', name: 'Found Property', plain: 'Found property', against: 'none', group: 'NC', category: 'non-criminal', words: ['found', 'found property', 'turned in'] },
  { code: 'NC-LOST', name: 'Lost Property', plain: 'Lost property', against: 'none', group: 'NC', category: 'non-criminal', words: ['lost', 'lost property', 'misplaced'] },
  { code: 'NC-MISSING', name: 'Missing Person (Located / Known Whereabouts)', plain: 'Missing person, already located', against: 'none', group: 'NC', category: 'non-criminal', words: ['located', 'found safe', 'returned home'] },
  { code: 'NC-OVERDOSE', name: 'Overdose', plain: 'Overdose / drug medical call (no crime)', against: 'none', group: 'NC', category: 'non-criminal', words: ['overdose', 'od', 'overdosed', 'narcan', 'naloxone', 'fentanyl overdose'] },
  { code: 'NC-DEATH', name: 'Death Investigation', plain: 'Death investigation (natural, unattended)', against: 'none', group: 'NC', category: 'non-criminal', words: ['death', 'deceased', 'unattended death', 'natural death', 'overdose death'] },
  { code: 'NC-WELFARE', name: 'Welfare Check / Mental Health', plain: 'Welfare check / mental health call', against: 'none', group: 'NC', category: 'non-criminal', words: ['welfare check', 'mental', 'emergency custody', 'well being'] },
  { code: 'NC-COLLISION', name: 'Traffic Collision (Non-Criminal)', plain: 'Traffic collision, no crime', against: 'none', group: 'NC', category: 'non-criminal', words: ['wreck', 'accident', 'collision', 'crash', 'hit deer'] },
  { code: 'NC-ASSIST', name: 'Assist Other Agency', plain: 'Assist another agency', against: 'none', group: 'NC', category: 'non-criminal', words: ['assist', 'mutual aid', 'assist other agency'] },
];

export const OFFENSE_BY_CODE = new Map(OFFENSES.map((o) => [o.code, o]));

export const CATEGORY_LABELS: Record<OffenseDef['category'], string> = {
  violent: 'Violent / against a person',
  sex: 'Sex offenses',
  theft: 'Theft & burglary',
  fraud: 'Fraud',
  damage: 'Damage & arson',
  drugs: 'Drugs',
  weapons: 'Weapons',
  vice: 'Vice & gambling',
  other: 'Other',
  'arrest-only': 'Arrest-only offenses',
  'non-criminal': 'No crime / information',
};

/** Find offenses for what the deputy typed: "broke into shed and took mower" → burglary, theft. */
export function searchOffenses(q: string, limit = 8): OffenseDef[] {
  const t = q.toLowerCase().replace(/[^a-z0-9& ]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t) return [];
  const words = t.split(' ').filter((w) => w.length > 1);
  const scored = OFFENSES.map((o) => {
    let s = 0;
    if (o.code.toLowerCase() === t) s += 100;
    const plain = o.plain.toLowerCase();
    const name = o.name.toLowerCase();
    if (plain.startsWith(t) || name.startsWith(t)) s += 30;
    for (const w of o.words) {
      if (t.includes(w)) s += 12 + w.length; // whole phrase in what they typed
      else if (w.includes(' ') && w.split(' ').every((x) => words.some((y) => y.startsWith(x.slice(0, Math.max(3, x.length - 2)))))) s += 6 + w.length;
      else if (!w.includes(' ') && words.some((y) => y.length >= 4 && y.startsWith(w.slice(0, Math.max(4, w.length - 2))))) s += 5;
    }
    for (const w of words) if (w.length >= 3 && (plain.includes(w) || name.includes(w))) s += 4;
    return { o, s };
  }).filter((x) => x.s > 0);
  scored.sort((a, b) => b.s - a.s || a.o.plain.localeCompare(b.o.plain));
  return scored.slice(0, limit).map((x) => x.o);
}

type CodeList = { code: string; label: string }[];
const list = (pairs: [string, string][]): CodeList => pairs.map(([code, label]) => ({ code, label }));

/** Data Element 9. The common ones first (shown as buttons); the rest under "More". */
export const LOCATION_TYPES = list([
  ['20', 'Home / residence (house)'],
  ['28', 'Apartment / condo'],
  ['13', 'Road / street / sidewalk'],
  ['18', 'Parking lot / garage'],
  ['07', 'Convenience store'],
  ['23', 'Gas station'],
  ['10', 'Field / woods'],
  ['53', 'School (K-12)'],
  ['05', 'Office / commercial building'],
  ['08', 'Department / discount store'],
  ['12', 'Grocery store'],
  ['21', 'Restaurant'],
  ['03', 'Bar / nightclub'],
  ['14', 'Hotel / motel'],
  ['50', 'Park / playground'],
  ['04', 'Church / place of worship'],
  ['09', 'Drug store / doctor / hospital'],
  ['11', 'Government / public building'],
  ['30', 'Court / courthouse'],
  ['15', 'Jail / prison'],
  ['16', 'Lake / river / beach'],
  ['17', 'Liquor store'],
  ['19', 'Storage unit facility'],
  ['24', 'Specialty store'],
  ['02', 'Bank'],
  ['01', 'Bus / train / air terminal'],
  ['06', 'Construction site'],
  ['37', 'Abandoned / condemned building'],
  ['38', 'Amusement park'],
  ['39', 'Arena / stadium / fairgrounds'],
  ['40', 'ATM (not at a bank)'],
  ['41', 'Car dealership'],
  ['42', 'Camp / campground'],
  ['44', 'Daycare'],
  ['45', 'Dock / freight terminal'],
  ['46', 'Farm facility'],
  ['47', 'Casino / race track / gambling'],
  ['48', 'Industrial site'],
  ['49', 'Military base'],
  ['51', 'Rest area'],
  ['52', 'College / university'],
  ['54', 'Shelter / mission'],
  ['55', 'Shopping mall'],
  ['56', 'Tribal lands'],
  ['57', 'Community center'],
  ['58', 'Online / cyberspace'],
  ['25', 'Other / unknown'],
]);
/** SC adds 28 Apartments/Condominiums (20 is houses only) and 30 Courts/Courthouses (pp.114–117). */
export const COMMON_LOCATIONS = ['20', '28', '13', '18', '07', '23', '10', '53', '05'];

/** Data Element 14 (p.134). Stolen includes robbed, defrauded, embezzled, extorted; Seized is property that wasn't stolen; Burned includes firefighting damage. */
export const LOSS_TYPES = list([
  ['7', 'Stolen'],
  ['4', 'Damaged / destroyed'],
  ['5', 'Recovered'],
  ['6', 'Seized'],
  ['2', 'Burned'],
  ['3', 'Counterfeited / forged'],
  ['1', 'None'],
  ['8', 'Unknown'],
]);

/** Data Element 15. */
export const PROPERTY_TYPES = list([
  ['20', 'Money / cash'],
  ['75', 'Phone / tablet / portable electronic communications'],
  ['07', 'Computer / laptop / software'],
  ['26', 'TV / radio / stereo (not car radios)'],
  ['17', 'Jewelry / precious metals'],
  ['13', 'Firearm (not BB / pellet guns)'],
  ['59', 'Firearm accessories (cases, scopes, holsters)'],
  ['36', 'Tools'],
  ['68', 'Lawn / yard / garden equipment'],
  ['16', 'Household goods'],
  ['19', 'Merchandise (store goods)'],
  ['25', 'Purse / wallet'],
  ['09', 'Credit / debit card (not gift cards)'],
  ['65', 'ID documents (license, SSN card)'],
  ['66', 'Identity (intangible)'],
  ['21', 'Endorsed checks, money orders, bearer bonds'],
  ['22', 'Blank / unendorsed checks, other non-negotiables'],
  ['03', 'Car'],
  ['37', 'Truck'],
  ['24', 'Other motor vehicle (motorcycle, ATV)'],
  ['28', 'Motor home / RV (towed campers are trailers)'],
  ['05', 'Bus'],
  ['78', 'Trailer'],
  ['38', 'Vehicle parts (catalytic converter, tires, car radio)'],
  ['39', 'Boat / watercraft'],
  ['79', 'Boat equipment'],
  ['04', 'Bicycle'],
  ['10', 'Drugs'],
  ['11', 'Drug equipment'],
  ['02', 'Alcohol'],
  ['08', 'Consumable goods'],
  ['06', 'Clothes / furs'],
  ['64', 'Fuel'],
  ['71', 'Metals (copper, scrap)'],
  ['43', 'Building materials'],
  ['15', 'Heavy equipment'],
  ['12', 'Farm equipment'],
  ['18', 'Livestock'],
  ['73', 'Pets'],
  ['47', 'Crops (not drug crops)'],
  ['44', 'Camping / hunting / fishing gear'],
  ['76', 'Sports / recreation equipment'],
  ['72', 'Musical instruments'],
  ['74', 'Camera / optical equipment'],
  ['23', 'Office equipment'],
  ['70', 'Medical equipment'],
  ['46', 'Collectibles'],
  ['48', 'Documents (personal / business)'],
  ['42', 'Art supplies'],
  ['45', 'Chemicals'],
  ['49', 'Explosives & ammunition'],
  ['80', 'Other weapons'],
  ['67', 'Law enforcement equipment'],
  ['69', 'Logging equipment'],
  ['14', 'Gambling equipment'],
  ['27', 'Recordings (audio / video)'],
  ['01', 'Aircraft'],
  ['41', 'Aircraft parts'],
  ['29', 'Building: single-family home'],
  ['30', 'Building: other dwelling'],
  ['31', 'Building: commercial / business'],
  ['32', 'Building: industrial'],
  ['33', 'Building: public / community'],
  ['34', 'Building: storage (shed, barn)'],
  ['35', 'Building: other'],
  ['77', 'Other'],
  ['88', 'Pending inventory'],
]);
export const VEHICLE_PROPERTY = ['03', '05', '24', '28', '37'];

/** Data Element 20, 21, 22. */
export const DRUG_TYPES = list([
  ['E', 'Marijuana'],
  ['L', 'Methamphetamine / amphetamine'],
  ['A', 'Crack cocaine'],
  ['B', 'Cocaine (not crack)'],
  ['H', 'Other narcotics (fentanyl, oxycodone, codeine, methadone…)'],
  ['D', 'Heroin'],
  ['O', 'Other depressants'],
  ['N', 'Barbiturates'],
  ['M', 'Other stimulants'],
  ['I', 'LSD'],
  ['J', 'PCP'],
  ['K', 'Other hallucinogens (MDMA, mushrooms, spice / K2)'],
  ['C', 'Hashish'],
  ['F', 'Morphine'],
  ['G', 'Opium'],
  ['P', 'Other drugs'],
  ['U', 'Unknown type (lab pending)'],
  ['X', 'More than 3 drug types'],
]);
/** SC data element S10 (drug type for a drug arrest) also has Q = Benzodiazepines (p.189). */
export const ARREST_DRUG_TYPES: CodeList = [...DRUG_TYPES.filter((d) => d.code !== 'U' && d.code !== 'X'), { code: 'Q', label: 'Benzodiazepines (Xanax, Valium…)' }, { code: 'U', label: 'Unknown type' }];
export const DRUG_UNITS = list([
  ['GM', 'grams'],
  ['KG', 'kilograms'],
  ['OZ', 'ounces'],
  ['LB', 'pounds'],
  ['DU', 'dosage units (pills)'],
  ['NP', 'plants'],
  ['ML', 'milliliters'],
  ['LT', 'liters'],
  ['FO', 'fluid ounces'],
  ['GL', 'gallons'],
  ['XX', 'not reported yet (lab pending)'],
]);

/** Data Element 13. */
export const WEAPONS = list([
  ['40', 'Hands, fists, feet (personal weapons)'],
  ['12', 'Handgun'],
  ['13', 'Rifle'],
  ['14', 'Shotgun'],
  ['11', 'Firearm (type unknown)'],
  ['15', 'Other firearm'],
  ['20', 'Knife / cutting instrument'],
  ['30', 'Blunt object (bat, club)'],
  ['35', 'Motor vehicle / vessel'],
  ['85', 'Asphyxiation (strangling, drowning, smothering)'],
  ['65', 'Fire / incendiary device'],
  ['60', 'Explosives'],
  ['50', 'Poison'],
  ['70', 'Drugs / sleeping pills'],
  ['90', 'Other'],
  ['95', 'Unknown'],
  ['99', 'None (must be the only choice)'],
]);
export const SIMPLE_ASSAULT_WEAPONS = ['40', '90', '95', '99'];
export const FIREARM_WEAPONS = ['11', '12', '13', '14', '15'];

/** Data Element 31 (aggravated assault / homicide circumstances). */
export const ASSAULT_CIRCUMSTANCES = list([
  ['01', 'Argument'],
  ['02', 'Assault on a law enforcement officer'],
  ['03', 'Drug dealing'],
  ['04', 'Gangland'],
  ['05', 'Juvenile gang'],
  ['06', 'Domestic violence'],
  ['07', 'Mercy killing (homicide only)'],
  ['08', 'Other felony involved'],
  ['09', 'Other circumstances'],
  ['10', 'Unknown circumstances (must be the only choice)'],
]);
export const NEGLIGENT_CIRCUMSTANCES = list([
  ['30', 'Child playing with weapon'],
  ['31', 'Gun-cleaning accident'],
  ['32', 'Hunting accident'],
  ['33', 'Other negligent weapon handling'],
  ['34', 'Other negligent killing'],
]);
export const JUSTIFIABLE_CIRCUMSTANCES = list([
  ['20', 'Criminal killed by private citizen'],
  ['21', 'Criminal killed by police officer'],
]);
/** Data Element 32: additional justifiable homicide circumstances (required for 09C, pp.32, 168). */
export const JUSTIFIABLE_DETAIL = list([
  ['A', 'Criminal attacked an officer, and that officer killed the criminal'],
  ['B', 'Criminal attacked an officer, and another officer killed the criminal'],
  ['C', 'Criminal attacked a civilian'],
  ['D', 'Criminal attempted flight from a crime'],
  ['E', 'Criminal killed in commission of a crime'],
  ['F', 'Criminal resisted arrest'],
  ['G', 'Unable to determine / not enough information'],
]);
/** SC data element S6: offense special circumstances (up to 3, pp.131–133). */
export const SPECIAL_CIRCUMSTANCES = list([
  ['P', 'Victim is a police officer'],
  ['B', 'Bomb threat'],
  ['C', 'Carjacking (robbery only)'],
  ['G', 'Gas drive-off'],
  ['H', 'Hate / bias motivated'],
  ['I', 'Offender suspected mentally ill'],
  ['J', 'Joy ride'],
  ['M', 'Mutilated'],
  ['S', 'Offender committed suicide'],
  ['X', 'Safe cracking'],
  ['Y', 'Youth gang activity'],
]);

/** Data Element 12. */
export const CRIMINAL_ACTIVITY = list([
  ['P', 'Possessing / concealing'],
  ['D', 'Distributing / selling'],
  ['U', 'Using / consuming'],
  ['B', 'Buying / receiving'],
  ['C', 'Cultivating / manufacturing / publishing'],
  ['T', 'Transporting / transmitting / importing'],
  ['O', 'Operating / promoting / assisting'],
  ['E', 'Exploiting children'],
]);
export const ANIMAL_CRUELTY_ACTIVITY = list([
  ['A', 'Simple / gross neglect'],
  ['I', 'Intentional abuse and torture'],
  ['F', 'Organized abuse (dog fighting, cock fighting)'],
  ['S', 'Animal sexual abuse'],
]);
export const GANG_INFO = list([
  ['N', 'None / unknown (must be the only choice)'],
  ['J', 'Juvenile gang'],
  ['G', 'Other gang'],
]);

/** Data Element 8 (p.107: A, C, D; N = not applicable is the FBI's value for "none of these"). */
export const SUSPECTED_USING = list([
  ['A', 'Alcohol'],
  ['D', 'Drugs / narcotics'],
  ['C', 'Computer equipment / phone'],
  ['N', 'None of these'],
]);

/** Data Element 8A. */
export const BIAS = list([
  ['88', 'None (no bias)'],
  ['99', 'Unknown (not determined yet; update later)'],
  ['11', 'Anti-White'],
  ['12', 'Anti-Black or African American'],
  ['13', 'Anti-American Indian or Alaska Native'],
  ['14', 'Anti-Asian'],
  ['15', 'Anti-Multiple Races, Group'],
  ['16', 'Anti-Native Hawaiian or Other Pacific Islander'],
  ['31', 'Anti-Arab'],
  ['32', 'Anti-Hispanic or Latino'],
  ['33', 'Anti-Other Race/Ethnicity/Ancestry'],
  ['21', 'Anti-Jewish'],
  ['22', 'Anti-Catholic'],
  ['23', 'Anti-Protestant'],
  ['24', 'Anti-Islamic (Muslim)'],
  ['25', 'Anti-Other Religion'],
  ['26', 'Anti-Multiple Religions, Group'],
  ['27', 'Anti-Atheism/Agnosticism'],
  ['81', 'Anti-Eastern Orthodox (Greek, Russian, etc.)'],
  ['82', 'Anti-Other Christian'],
  ['83', 'Anti-Buddhist'],
  ['84', 'Anti-Hindu'],
  ['85', 'Anti-Sikh'],
  ['28', 'Anti-Church of Jesus Christ'],
  ['29', 'Anti-Jehovah’s Witness'],
  ['41', 'Anti-Gay (Male)'],
  ['42', 'Anti-Lesbian (Female)'],
  ['43', 'Anti-Lesbian, Gay, Bisexual, or Transgender (Mixed Group)'],
  ['44', 'Anti-Heterosexual'],
  ['45', 'Anti-Bisexual'],
  ['71', 'Anti-Transgender'],
  ['72', 'Anti-Gender Non-Conforming'],
  ['51', 'Anti-Physical Disability'],
  ['52', 'Anti-Mental Disability'],
  ['61', 'Anti-Male'],
  ['62', 'Anti-Female'],
]);

/** Data Element 25. */
export const VICTIM_TYPES = list([
  ['I', 'Person (individual)'],
  ['B', 'Business'],
  ['L', 'Law enforcement officer (murder or assault only)'],
  ['G', 'Government'],
  ['F', 'Bank / financial institution'],
  ['R', 'Church / religious organization'],
  ['S', 'Society / the public'],
  ['O', 'Other'],
  ['U', 'Unknown'],
]);

/** LEOKA, Data Element 25A: what the officer was doing (pp.158–159). */
export const LEOKA_ACTIVITY = list([
  ['01', 'Responding to a disturbance call (family dispute, person with a gun…)'],
  ['02', 'Burglary in progress / pursuing a burglary suspect'],
  ['03', 'Robbery in progress / pursuing a robbery suspect'],
  ['04', 'Attempting other arrests'],
  ['05', 'Civil disorder (riot, mass protest)'],
  ['06', 'Handling, transporting, custody of prisoners'],
  ['07', 'Investigating suspicious persons or circumstances'],
  ['08', 'Ambush (no warning)'],
  ['09', 'Handling a person with mental illness'],
  ['10', 'Traffic pursuit or stop'],
  ['11', 'All other'],
]);
/** LEOKA, Data Element 25B: the officer's assignment (p.159). */
export const LEOKA_ASSIGNMENT = list([
  ['G', 'One-officer vehicle, alone (uniformed)'],
  ['H', 'One-officer vehicle, assisted (uniformed)'],
  ['F', 'Two-officer vehicle (uniformed)'],
  ['I', 'Detective / special assignment, alone'],
  ['J', 'Detective / special assignment, assisted'],
  ['K', 'Other, alone (foot patrol, off duty)'],
  ['L', 'Other, assisted (foot patrol, off duty)'],
]);

/** Data Element 33. */
export const INJURIES = list([
  ['N', 'None'],
  ['M', 'Minor injury'],
  ['B', 'Apparent broken bones'],
  ['L', 'Severe laceration'],
  ['I', 'Possible internal injury'],
  ['T', 'Loss of teeth'],
  ['U', 'Unconsciousness'],
  ['O', 'Other major injury'],
]);

/** Data Element 35: the victim is the suspect's … */
export const RELATIONSHIPS = list([
  ['ST', 'Stranger'],
  ['AQ', 'Acquaintance'],
  ['FR', 'Friend'],
  ['NE', 'Neighbor'],
  ['BG', 'Boyfriend / girlfriend'],
  ['XR', 'Ex-boyfriend / ex-girlfriend'],
  ['SE', 'Spouse'],
  ['CS', 'Common-law spouse'],
  ['XS', 'Ex-spouse'],
  ['PA', 'Parent'],
  ['CH', 'Child'],
  ['SB', 'Sibling'],
  ['GP', 'Grandparent'],
  ['GC', 'Grandchild'],
  ['IL', 'In-law'],
  ['SP', 'Stepparent'],
  ['SC', 'Stepchild'],
  ['SS', 'Stepsibling'],
  ['OF', 'Other family member'],
  ['CF', 'Child of boyfriend / girlfriend'],
  ['BE', 'Babysittee (child in their care)'],
  ['EE', 'Employee'],
  ['ER', 'Employer'],
  ['OK', 'Otherwise known'],
  ['VO', 'Victim was the offender'],
  ['RU', 'Relationship unknown'],
]);
export const FAMILY_RELATIONSHIPS = ['SE', 'CS', 'XS', 'PA', 'CH', 'SB', 'GP', 'GC', 'IL', 'SP', 'SC', 'SS', 'OF', 'BG', 'XR', 'CF'];

export const SEXES = list([
  ['M', 'Male'],
  ['F', 'Female'],
  ['U', 'Unknown'],
]);
export const RACES = list([
  ['W', 'White'],
  ['B', 'Black / African American'],
  ['A', 'Asian'],
  ['I', 'American Indian / Alaska Native'],
  ['P', 'Native Hawaiian / Pacific Islander'],
  ['U', 'Unknown'],
]);
export const ETHNICITIES = list([
  ['N', 'Not Hispanic or Latino'],
  ['H', 'Hispanic or Latino'],
  ['U', 'Unknown'],
]);
export const RESIDENT_STATUS = list([
  ['R', 'Lives in our jurisdiction'],
  ['N', 'Does not live in our jurisdiction'],
  ['U', 'Unknown'],
]);

/** Data Element 4: exceptional clearance (p.106). All four must be true: offender known, probable cause, location known, and something outside our control prevents arrest. */
export const EXCEPTIONAL_CLEARANCE = list([
  ['A', 'Death of the offender'],
  ['B', 'Prosecution declined (not for lack of probable cause)'],
  ['C', 'In custody of another jurisdiction (extradition denied)'],
  ['D', 'Victim refused to cooperate'],
  ['E', 'Juvenile, no custody'],
]);

/** Data Element 42 / 43. */
export const ARREST_TYPES = list([
  ['T', 'Taken into custody (warrant or earlier report)'],
  ['O', 'On-view arrest (no warrant, no earlier report)'],
  ['S', 'Summoned / cited'],
]);

export const labelOf = (l: CodeList, code: string | null | undefined) => l.find((x) => x.code === code)?.label ?? code ?? '';

/** Data Element 46: arrestee was armed with (up to 2). */
export const ARRESTEE_ARMED = list([
  ['01', 'Unarmed'],
  ['12', 'Handgun'],
  ['13', 'Rifle'],
  ['14', 'Shotgun'],
  ['11', 'Firearm (type unknown)'],
  ['15', 'Other firearm'],
  ['16', 'Lethal cutting instrument (knife)'],
  ['17', 'Club / blackjack / brass knuckles'],
]);

/** SC data element S9: what a drug arrest was for (one, p.187). */
export const DRUG_ARREST_ACTIVITY = list([
  ['P', 'Possessing / concealing'],
  ['D', 'Distributing / selling'],
  ['U', 'Using / consuming'],
  ['B', 'Buying / receiving'],
  ['C', 'Cultivating / manufacturing'],
  ['T', 'Transporting / importing'],
  ['O', 'Operating / promoting / assisting'],
  ['E', 'Exploiting children'],
]);

/** Data Element 52: disposition of an arrestee under 18 (required even if charged as an adult, p.185). */
export const JUVENILE_DISPOSITION = list([
  ['H', 'Handled within the department (released to parents, warning)'],
  ['R', 'Referred to family court / other authorities'],
]);

export const HAIR_COLORS = ['Black', 'Brown', 'Blond', 'Red', 'Gray', 'White', 'Bald', 'Other'];
export const EYE_COLORS = ['Brown', 'Blue', 'Green', 'Hazel', 'Gray', 'Black', 'Other'];
